import { defineStore } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { computed, ref, shallowRef } from 'vue'
import type { CategoryShort } from '@/5_entities/Category'
import { cache, getMilliseconds } from '@/6_shared/lib'
import { EntityStatus } from '@/6_shared/model'
import { configurationService } from '../api'
import { partnerKey } from '../lib'
import type { ConfigurationDTO } from './types'

export const useConfiguration = defineStore('configuration', () => {
  //global ready param
  const appIsReady = ref(false)
  const initApp = () => (appIsReady.value = true)

  //app update property
  const appUpdateKey = ref(0)
  const globalLoading = ref(false)
  const showGlobalPreloader = ref(false)
  const loadingTimer = ref<Undefinable<NodeJS.Timeout>>(undefined)

  const updateAppOnCriticalParamsChange = async <T>(callback: Promise<T>) => {
    cache.clear()
    globalLoading.value = true

    loadingTimer.value = setTimeout(() => {
      showGlobalPreloader.value = true
    }, getMilliseconds.inSeconds(0.5))

    await callback

    clearTimeout(loadingTimer.value)
    appUpdateKey.value++
    globalLoading.value = false
    showGlobalPreloader.value = false
  }

  //app config
  const configuration = shallowRef<Undefinable<ConfigurationDTO>>(undefined)
  const getConfiguration = async (partnerKeyString: string) => {
    try {
      configuration.value = await configurationService.get(partnerKeyString)
      setAppCategories(configuration.value.categories)

      return configuration.value
    } catch (error) {
      partnerKey.handleEmpty()
    }
  }
  //app categories
  const appCategoriesMap = ref<Map<CategoryShort['id'], CategoryShort>>(new Map())
  const setAppCategories = (categories: CategoryShort[]) => {
    categories.sort((a, b) => b.rank - a.rank)

    categories.forEach((category) => {
      appCategoriesMap.value.set(category.id, category)
    })
  }
  const getCategoryFromHandbook = (categoryId: CategoryShort['id']): Undefinable<CategoryShort> => {
    const fromMap = appCategoriesMap.value.get(categoryId)

    if (!fromMap || fromMap.status === EntityStatus.disabled) return undefined

    return fromMap
  }

  const categoriesNav = computed((): CategoryShort[] => {
    return Array.from(appCategoriesMap.value.values())?.filter(
      (item) => item.id > 0 && item.status !== EntityStatus.disabled
    )
  })

  //shared
  const themePrefix = computed(() => '')
  const locations = computed(() => {
    if (!configuration.value?.locations) return

    const arr = [...configuration.value.locations]

    arr.sort(({ name: nameA }, { name: nameB }) => {
      return nameA.localeCompare(nameB)
    })

    return arr
  })

  return {
    appIsReady,
    initApp,

    appUpdateKey,
    globalLoading,
    showGlobalPreloader,
    updateAppOnCriticalParamsChange,

    configuration,
    getConfiguration,

    themePrefix,
    locations,
    categoriesNav,
    getCategoryFromHandbook
  }
})
